export const WAP_LISTING_DETAIL_VERSION_KEY = 'wap_listing_detail_version'
const WAP_LISTING_DETAIL_VERSION_EXPIRE_KEY = 'wap_listing_detail_version_expire'
const VERSION_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000
const CONFIG_URL = 'https://image.kilimall.com/lite/new_wap/wap_listing_detail_version.json'

export type WapListingDetailVersion = '1' | '2'

export interface WapListingDetailVersionConfig {
  enableExtremeDiversion: boolean
  extremeDiversionRatio: number
}

const DEFAULT_CONFIG: WapListingDetailVersionConfig = {
  enableExtremeDiversion: true,
  extremeDiversionRatio: 30,
}

let cachedConfig: WapListingDetailVersionConfig | null = null
let configFetchPromise: Promise<WapListingDetailVersionConfig> | null = null
let initializationPromise: Promise<void> | null = null

export const isListingDetailVersionRoute = (path: string) => /^\/listing\//.test(path)

const normalizeConfig = (data: Partial<WapListingDetailVersionConfig>): WapListingDetailVersionConfig => ({
  enableExtremeDiversion: data.enableExtremeDiversion ?? DEFAULT_CONFIG.enableExtremeDiversion,
  extremeDiversionRatio: Math.min(100, Math.max(0, Number(data.extremeDiversionRatio ?? DEFAULT_CONFIG.extremeDiversionRatio))),
})

/** 每次应用启动拉取最新开关配置，同一会话内复用内存缓存 */
const fetchConfig = async (force = false): Promise<WapListingDetailVersionConfig> => {
  if (!force && cachedConfig) return cachedConfig
  if (configFetchPromise) return configFetchPromise

  configFetchPromise = fetch(CONFIG_URL, { cache: 'no-cache' })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    })
    .then((data) => {
      cachedConfig = normalizeConfig(data)
      return cachedConfig
    })
    .catch(() => {
      cachedConfig = DEFAULT_CONFIG
      return DEFAULT_CONFIG
    })
    .finally(() => {
      configFetchPromise = null
    })

  return configFetchPromise
}

const resolveVersion = (config: WapListingDetailVersionConfig): WapListingDetailVersion => {
  const random = Math.floor(Math.random() * 100) + 1
  return random <= config.extremeDiversionRatio ? '2' : '1'
}

/** 读取 localStorage 中已分配的用户版本（7 天有效） */
const getStoredWapListingDetailVersion = (): WapListingDetailVersion | null => {
  const version = localStorage.getItem(WAP_LISTING_DETAIL_VERSION_KEY)
  const expireAt = Number(localStorage.getItem(WAP_LISTING_DETAIL_VERSION_EXPIRE_KEY))

  if (!version || !['1', '2'].includes(version) || !expireAt || Date.now() > expireAt) {
    return null
  }

  return version as WapListingDetailVersion
}

/** 响应式版本状态：供组件依赖，确保异步分配后视图自动更新 */
export const useWapListingDetailVersionState = () =>
  useState<WapListingDetailVersion | null>('wap-listing-detail-version', () => null)

/** 获取当前生效的版本：开关关闭时统一返回普通版 */
export const getWapListingDetailVersion = (): WapListingDetailVersion | null => {
  if (!process.client) return null

  if (cachedConfig && !cachedConfig.enableExtremeDiversion) {
    return '1'
  }

  return getStoredWapListingDetailVersion()
}

/** 应用启动时：拉取最新开关配置；安卓浏览器 / Facebook·WhatsApp 内置浏览器下按需分配用户版本（7 天有效） */
export const initWapListingDetailVersion = async () => {
  if (!process.client) return

  const state = useWapListingDetailVersionState()
  const config = await fetchConfig(true)

  // 开关关闭：统一普通版
  if (!config.enableExtremeDiversion) {
    state.value = '1'
    return
  }

  const stored = getStoredWapListingDetailVersion()
  const win = window as Window & { androidWebObject?: unknown; flutter_inappwebview?: unknown }
  const isInKilimallApp = !!(win.androidWebObject || win.flutter_inappwebview)
  const isAndroidBrowser = /Android/i.test(navigator.userAgent) && !isInKilimallApp
  const isFbOrWaInApp = isFacebook().value || isWhatsApp().value
    || /FBAV|FBAN/i.test(navigator.userAgent) || /WhatsApp/i.test(navigator.userAgent)
  const shouldDivert = (isAndroidBrowser || isFbOrWaInApp) && !isInKilimallApp

  if (!shouldDivert) {
    state.value = stored
    return
  }

  if (stored) {
    state.value = stored
    return
  }

  const version = resolveVersion(config)

  localStorage.setItem(WAP_LISTING_DETAIL_VERSION_KEY, version)
  localStorage.setItem(WAP_LISTING_DETAIL_VERSION_EXPIRE_KEY, String(Date.now() + VERSION_CACHE_TTL_MS))
  state.value = version
}

/** One startup request; detail navigation joins it before rendering either version. */
export const ensureWapListingDetailVersion = (): Promise<void> => {
  if (!process.client) return Promise.resolve()
  if (!initializationPromise) {
    initializationPromise = initWapListingDetailVersion().catch(error => {
      initializationPromise = null
      throw error
    })
  }
  return initializationPromise
}
